import abTranslate from 'ab-translate/dist/src/abTranslate-runtime'
import { createInstance } from 'i18next'
import type { Logger } from '../logger'
import { UNKNOWN_VERSION } from '../appUrlUtils'
import type { ExperimentsBag } from '@wix/wix-experiments'

const promiseRetry = async (method: () => any, numberOfRetries: number) => {
  let lastError = null
  for (let index = 0; index < numberOfRetries; index++) {
    try {
      return await method()
    } catch (error) {
      lastError = error
    }
  }
  throw lastError
}

type Fallback = ((error: unknown) => void) | undefined

type OrDeferred<T> = T | (() => T | Promise<T>)

const i18nCreator = ({
  modules = [],
  resource = {},
}: {
  modules?: any[]
  resource?: OrDeferred<Record<string, string>>
}) => {
  const i18nInstance = createInstance({ initImmediate: false })
  modules.forEach(m => i18nInstance.use(m))
  const initFunc = i18nInstance.init.bind(i18nInstance)
  const init = async function ({ lng: locale = 'en' } = {}) {
    return new Promise((resolve, reject) => {
      const addResources = (resolvedResource: Record<string, string>) => {
        i18nInstance.addResources(locale, 'translation', resolvedResource)

        resolve(i18nInstance)
      }
      initFunc(
        {
          lng: locale,
          fallbackLng: 'en',
          keySeparator: false,
          interpolation: {
            escapeValue: false,
            prefix: '<%=',
            suffix: '%>',
          },
        },
        (err: any) => {
          if (err) {
            reject(err)
            return
          }
          if (typeof resource === 'object') {
            addResources(resource)
            return
          }
          const resourceResult = resource()
          if (resourceResult instanceof Promise) {
            resourceResult.then(addResources)
          } else {
            addResources(resourceResult)
          }
        },
      )
    })
  }
  return Object.assign(i18nInstance, { init })
}

const fetchLocale = async (url: string) => {
  try {
    const res = await fetch(url)
    if (res.ok) {
      return await res.text()
    } else {
      throw new Error(
        `Bad status code: ${res.status} (text: ${res.statusText})`,
      )
    }
  } catch (error: any) {
    throw new Error(
      `Failed fetching '${url}'. Error: '${error && error.message}'`,
    )
  }
}

const fetchWithRetry = (url: string) => promiseRetry(() => fetchLocale(url), 2)

interface ReadParams {
  url: string
  language: string
  englishTranslations: any
  onFallback: Fallback
}

const read = async ({
  url,
  language,
  englishTranslations,
  onFallback,
}: ReadParams) => {
  if (englishTranslations && language === 'en') {
    return englishTranslations
  }

  try {
    return JSON.parse(await fetchWithRetry(url))
  } catch (error: any) {
    if (englishTranslations) {
      if (onFallback) {
        onFallback(
          new Error('Falling back to English due to Error', { cause: error }),
        )
      }
      return englishTranslations
    } else {
      throw new Error('Failed to read translations', { cause: error })
    }
  }
}

const createFetchModule = (
  baseUrl: string,
  englishTranslations: Record<string, string> | undefined,
  onFallback: Fallback,
  getExperiments: () => Promise<ExperimentsBag>,
): any => ({
  read(
    language: string,
    _: string,
    callback: (
      err: any,
      translations: Record<string, string> | boolean,
    ) => void,
  ) {
    Promise.all([
      getExperiments ? getExperiments() : null,
      read({
        url: `${baseUrl}/messages_${language}.json`,
        language,
        onFallback,
        englishTranslations,
      }),
    ]).then(
      ([experiments, translations]) =>
        callback(
          null,
          experiments ? abTranslate(experiments, translations) : translations,
        ),
      error => callback(error, false),
    )
  },
  //this is needed for i18n to correctly use this module
  type: 'backend',
})

type Translations = Record<string, string> | undefined

type GetExperiments = () => Promise<ExperimentsBag>

export const createI18nFromResource = (
  resource: OrDeferred<Record<string, string>>,
) => i18nCreator({ resource })

const createI18n = (
  baseUrl: string,
  englishTranslations: Translations,
  onFallback: Fallback,
  getExperiments: GetExperiments,
) =>
  i18nCreator({
    modules: [
      createFetchModule(
        baseUrl,
        englishTranslations,
        onFallback,
        getExperiments,
      ),
    ],
  })

export const createI18nFromAppInfo = ({
  appName,
  appVersion,
  localeFolder,
  fallbackUrl,
  englishTranslations,
  getExperiments,
}: {
  appName: string
  appVersion: string
  localeFolder: string
  fallbackUrl: string
  englishTranslations: Translations
  getExperiments: GetExperiments
}) =>
  createI18n(
    appVersion !== UNKNOWN_VERSION
      ? `https://static.parastorage.com/services/${appName}/${appVersion}/${localeFolder}`
      : fallbackUrl,
    englishTranslations,
    undefined,
    getExperiments,
  )

export const createI18nFromUrl = ({
  staticsUrl,
  englishTranslations,
  logger,
  experiments,
}: {
  staticsUrl: string
  englishTranslations: Translations
  logger: Logger
  experiments: ExperimentsBag
}) =>
  createI18n(
    `${staticsUrl}locale/data-binding`,
    englishTranslations,
    (error: unknown) => logger.error(error),
    async () => experiments,
  )
